import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface TagInputProps {
  tags: string[];
  availableTags: string[];
  onTagsChange: (tags: string[]) => void;
  className?: string;
  placeholder?: string;
  maxTags?: number;
}

const TagInput: React.FC<TagInputProps> = ({
  tags,
  availableTags,
  onTagsChange,
  className = '',
  placeholder = 'Add tag...',
  maxTags = 10
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredTags, setFilteredTags] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter available tags based on input value and exclude already selected tags
  useEffect(() => {
    if (inputValue) {
      const filtered = availableTags.filter(tag =>
        tag.toLowerCase().includes(inputValue.toLowerCase()) &&
        !tags.includes(tag)
      );
      setFilteredTags(filtered);
    } else {
      // When input is empty, show all available tags that aren't already selected
      const filtered = availableTags.filter(tag => !tags.includes(tag));
      setFilteredTags(filtered);
    }
  }, [inputValue, availableTags, tags]);

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag) && tags.length < maxTags) {
      onTagsChange([...tags, trimmedTag]);
    }
    setInputValue('');
    setIsDropdownOpen(false);
  };

  const removeTag = (tagToRemove: string) => {
    onTagsChange(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      removeTag(tags[tags.length - 1]);
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      setInputValue('');
    } else if (e.key === 'ArrowDown' && filteredTags.length > 0) {
      e.preventDefault();
      // Focus first suggestion
      const firstSuggestion = document.querySelector('.tag-suggestion') as HTMLButtonElement;
      firstSuggestion?.focus();
    }
  };

  const handleSuggestionKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, tag: string, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(tag);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index === 0) {
        inputRef.current?.focus();
      } else {
        const prevSuggestion = document.querySelectorAll('.tag-suggestion')[index - 1] as HTMLButtonElement;
        prevSuggestion?.focus();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextSuggestion = document.querySelectorAll('.tag-suggestion')[index + 1] as HTMLButtonElement;
      if (nextSuggestion) {
        nextSuggestion.focus();
      }
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="w-full min-h-[42px] border border-edge-input rounded-md px-3 py-2 focus-within:ring-primary-500 focus-within:border-primary-500">
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800"
            >
              {tag}
              <button
                data-track="tag_remove"
                type="button"
                onClick={() => removeTag(tag)}
                className="ml-1 text-primary-600 hover:text-primary-900 focus:outline-none"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsDropdownOpen(true);
          }}
          onBlur={() => {
            // Delay hiding dropdown to allow clicking on suggestions
            setTimeout(() => setIsDropdownOpen(false), 200);
          }}
          placeholder={tags.length >= maxTags ? `Maximum ${maxTags} tags` : placeholder}
          disabled={tags.length >= maxTags}
          className="w-full border-none outline-none flex-1 min-w-0 bg-transparent"
        />
      </div>

      {/* Dropdown for suggestions */}
      {isDropdownOpen && (
        <div className="absolute z-10 w-full mt-1 bg-surface border border-edge-input rounded-md shadow-lg">
          {filteredTags.length > 0 ? (
            <ul className="max-h-40 overflow-y-auto">
              {filteredTags.map((tag, index) => (
                <li key={tag}>
                  <button
                    data-track="tag_suggestion"
                    type="button"
                    className="tag-suggestion w-full px-3 py-2 text-left hover:bg-primary-50 focus:bg-primary-50 focus:outline-none"
                    onClick={() => addTag(tag)}
                    onKeyDown={(e) => handleSuggestionKeyDown(e, tag, index)}
                  >
                    {tag}
                  </button>
                </li>
              ))}
            </ul>
          ) : inputValue ? (
            <div className="px-3 py-2">
              <button
                data-track="tag_add"
                type="button"
                className="tag-suggestion w-full text-left text-primary-600 hover:bg-primary-50 focus:bg-primary-50 focus:outline-none py-1"
                onClick={() => addTag(inputValue)}
                onKeyDown={(e) => handleSuggestionKeyDown(e, inputValue, 0)}
              >
                Create "{inputValue}"
              </button>
            </div>
          ) : filteredTags.length === 0 && availableTags.length > 0 ? (
            <div className="px-3 py-2 text-muted text-sm">
              All available tags have been added
            </div>
          ) : null}
        </div>
      )}

      {tags.length >= maxTags && (
        <p className="text-xs text-muted mt-1">
          Maximum of {maxTags} tags reached
        </p>
      )}
    </div>
  );
};

export default TagInput;